import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { 
  Car, LayoutDashboard, BarChart3, CreditCard, LogOut, Loader2,
  ArrowLeft, Eye, Target, DollarSign, Calendar, Clock, CheckCircle,
  AlertCircle, Play, Pause, Send, Image, Edit
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export default function CampaignDetail() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [advertiser, setAdvertiser] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (advertiser) {
      fetchCampaign();
      fetchPerformance();
    }
  }, [advertiser, campaignId]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/me`, { withCredentials: true });
      setAdvertiser(response.data);
    } catch (error) {
      navigate("/advertiser/login");
    }
  };

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/campaigns/${campaignId}`, { withCredentials: true });
      setCampaign(response.data);
    } catch (error) {
      toast.error("Campaign not found");
      navigate("/advertiser/campaigns");
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformance = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/campaigns/${campaignId}/performance`, { withCredentials: true });
      setPerformance(response.data);
    } catch {}
  };

  const handleSubmitForApproval = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/advertiser-portal/campaigns/${campaignId}/submit`, {}, { withCredentials: true });
      toast.success("Campaign submitted for approval!");
      fetchCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/advertiser-portal/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/advertiser/login");
  };

  const statusColors = {
    draft: "bg-slate-500/20 text-slate-400",
    pending_approval: "bg-amber-500/20 text-amber-400",
    approved: "bg-blue-500/20 text-blue-400",
    active: "bg-emerald-500/20 text-emerald-400",
    paused: "bg-orange-500/20 text-orange-400",
    completed: "bg-purple-500/20 text-purple-400",
    rejected: "bg-red-500/20 text-red-400"
  };

  const statusIcons = {
    draft: Edit,
    pending_approval: Clock,
    approved: CheckCircle,
    active: Play,
    paused: Pause,
    completed: CheckCircle,
    rejected: AlertCircle
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const StatusIcon = statusIcons[campaign?.status] || Clock;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="campaign-detail">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/advertiser/campaigns")}
                className="text-slate-400"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              <span className="text-slate-400">|</span>
              <span className="text-white font-medium truncate max-w-[200px]">{campaign?.name}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Campaign Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {campaign?.name}
              </h1>
              <Badge className={statusColors[campaign?.status]}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {campaign?.status?.replace("_", " ")}
              </Badge>
            </div>
            <p className="text-slate-400">{campaign?.description || "No description"}</p>
          </div>
          <div className="flex gap-3">
            {campaign?.status === "draft" && (
              <Button 
                onClick={handleSubmitForApproval}
                disabled={submitting || !campaign?.content_ids?.length}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-for-approval"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit for Approval
              </Button>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {campaign?.status === "draft" && (
          <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg mb-8">
            <p className="text-slate-300">
              <Edit className="w-4 h-4 inline mr-2" />
              This campaign is in draft. Add content and submit for approval when ready.
            </p>
          </div>
        )}
        {campaign?.status === "pending_approval" && (
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-8">
            <p className="text-amber-300">
              <Clock className="w-4 h-4 inline mr-2" />
              Your campaign is being reviewed. We'll notify you once approved.
            </p>
          </div>
        )}
        {campaign?.status === "rejected" && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg mb-8">
            <p className="text-red-300">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Campaign was not approved. Reason: {campaign?.rejection_reason || "Content did not meet guidelines"}
            </p>
          </div>
        )}

        {/* Performance Stats */}
        {performance && campaign?.status === "active" && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Impressions</p>
                    <p className="text-2xl font-bold text-white mt-1">{(performance.impressions || 0).toLocaleString()}</p>
                  </div>
                  <Eye className="w-8 h-8 text-blue-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Visit Scans</p>
                    <p className="text-2xl font-bold text-white mt-1">{(performance.visits || 0).toLocaleString()}</p>
                  </div>
                  <Target className="w-8 h-8 text-purple-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Credits Triggered</p>
                    <p className="text-2xl font-bold text-white mt-1">{(performance.credits_triggered || 0).toLocaleString()}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-emerald-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Spend</p>
                    <p className="text-2xl font-bold text-white mt-1">£{(performance.spend || 0).toFixed(2)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-amber-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Campaign Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Campaign Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Campaign Type</p>
                    <p className="text-white font-medium capitalize mt-1">
                      {campaign?.campaign_type?.replace("_", " ")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Target</p>
                    <p className="text-white font-medium mt-1">
                      {campaign?.target?.target_type === "network" 
                        ? "Network-wide" 
                        : `${campaign?.target?.site_ids?.length || campaign?.target?.screen_ids?.length || 0} ${campaign?.target?.target_type}s`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Start Date</p>
                    <p className="text-white font-medium mt-1">{campaign?.schedule?.start_date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">End Date</p>
                    <p className="text-white font-medium mt-1">{campaign?.schedule?.end_date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pricing Model</p>
                    <p className="text-white font-medium capitalize mt-1">
                      {campaign?.pricing?.model?.replace("_", " ")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Budget</p>
                    <p className="text-white font-medium mt-1">
                      £{campaign?.pricing?.total_budget || campaign?.pricing?.flat_rate || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Content */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Creative Content</CardTitle>
              </CardHeader>
              <CardContent>
                {campaign?.content_details?.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {campaign.content_details.map(content => (
                      <div key={content.content_id} className="aspect-video bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
                        {content.content_type === "video" ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-slate-400" />
                          </div>
                        ) : (
                          <img 
                            src={content.file_url}
                            alt={content.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Image className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No content uploaded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Performance Summary */}
            {performance && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-sm">Performance Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Effective CPM</span>
                    <span className="text-white font-medium">£{performance.cpm_effective}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Cost per Visit</span>
                    <span className="text-white font-medium">£{performance.cost_per_visit}</span>
                  </div>
                  {performance.budget_remaining !== null && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Budget Remaining</span>
                      <span className="text-white font-medium">£{performance.budget_remaining?.toFixed(2)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start border-slate-700 text-slate-300" asChild>
                  <Link to="/advertiser/campaigns/new">
                    <BarChart3 className="w-4 h-4 mr-2" />Create Another Campaign
                  </Link>
                </Button>
                <Button variant="outline" className="w-full justify-start border-slate-700 text-slate-300" asChild>
                  <Link to="/advertiser/billing">
                    <CreditCard className="w-4 h-4 mr-2" />View Billing
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
